import React, { useState, useEffect } from "react";
import Loader from "./components/common/Spinner";
import './App.css';
import LoginPage from "./components/base/LoginPage";
import "./helpers/interceptor";
import { AuthenticatedTemplate, UnauthenticatedTemplate, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import { authenticate, getUserInfo } from './services/user-service';
import Home from './components/base/Home';
import AccessCodeVerification from "./components/base/AccessCodeVerification";
import DepartmentSelection from "./components/base/DepartmentSelection";


function App() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts, inProgress } = useMsal();
  const [authState, setAuthState] = useState({
    validUser: false,
    accessCodeVerified: false,
    deptSelectionRequired: false,
    pendingAuth: null,
    allUserData: null,
    availableDepts: []
  });
  const [userData, setUserData] = useState(false);
  const [idToken, setIdToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    console.log("isAuthenticated in useeffects", isAuthenticated, idToken);

    // Don't clear session data if MSAL is still initializing
    if (inProgress !== "none") {
      return;
    }

    // Handle logout flag - skip all processing if logging out
    if (sessionStorage.getItem("isLoggingOut")) {
      sessionStorage.removeItem("isLoggingOut");
      return;
    }

    // Check if user has COMPLETE session (including access code verification)
    const existingUser = sessionStorage.getItem('User');
    const existingToken = sessionStorage.getItem('Token');
    const accessCodeVerified = sessionStorage.getItem('AccessCodeVerified');

    if (existingUser && existingToken && accessCodeVerified === 'true' && isAuthenticated) {
      try {
        const payload = JSON.parse(atob(existingToken.split('.')[1]));
        const currentTime = Math.floor(Date.now() / 1000);
        if (payload.exp && payload.exp > currentTime) {
          setAuthState(prev => ({ ...prev, validUser: true, accessCodeVerified: true }));
          setUserData(JSON.parse(existingUser));
          return;
        } else {
          sessionStorage.clear();
        }
      } catch (error) {
        sessionStorage.clear();
      }
    }

    // Check for pending authentication (before access code)
    const pendingUserData = sessionStorage.getItem('PendingUserData');
    if (pendingUserData && isAuthenticated && !authState.accessCodeVerified) {
      try {
        const pendingData = JSON.parse(pendingUserData);
        setAuthState(prev => ({ ...prev, pendingAuth: pendingData }));
        setUserData(pendingData);
        return;
      } catch (error) {
        sessionStorage.removeItem('PendingUserData');
      }
    }

    // Check for access code verified but department selection pending
    if (pendingUserData && isAuthenticated && authState.accessCodeVerified === true) {
      try {
        const pendingData = JSON.parse(pendingUserData);
        const storedAllUserData = sessionStorage.getItem('AllUserData');
        if (pendingData.requiresDeptSelection && !pendingData.DeptId && storedAllUserData) {
          // Restore department selection state
          const allUsers = JSON.parse(storedAllUserData);
          setAuthState({
            ...authState,
            allUserData: allUsers,
            availableDepts: allUsers,
            accessCodeVerified: true,
            deptSelectionRequired: true,
            pendingAuth: pendingData
          });
          setUserData(pendingData);
          return;
        }
      } catch (error) {
        sessionStorage.removeItem('PendingUserData');
        sessionStorage.removeItem('AllUserData');
      }
    }

    // Clear error message and reset state when user logs out
    if (!isAuthenticated) {
      setErrorMessage("");
      setAuthState({
        validUser: false,
        accessCodeVerified: false,
        deptSelectionRequired: false,
        pendingAuth: null,
        allUserData: null,
        availableDepts: []
      });
      setIdToken(null);
      setUserData(false);
      setLoading(false);
      sessionStorage.clear();
      return;
    }

    if (isAuthenticated && !idToken && !sessionStorage.getItem("isLoggingOut")) {
      setLoading(true);
      RequestProfileData();
    }
    if (idToken && idToken !== "" && !sessionStorage.getItem("isLoggingOut")) {
      sessionStorage.setItem("Token", idToken);
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      const expiryTime = new Date(payload.exp * 1000);
      console.log('Token expires at:', expiryTime);
      getUserData();
    }
  }, [isAuthenticated, idToken, inProgress]);

  function RequestProfileData() {
    const request = {
      ...loginRequest,
      account: accounts[0],
    };
    // Silently acquires an access token which is then attached to a request for Microsoft Graph data
    instance.acquireTokenSilent(request)
      .then((response) => {
        setIdToken(response.idToken);
        sessionStorage.setItem("Azure_AD_Access_Token", response.accessToken);
      })
      .catch((e) => {
        console.log("Silent token acquisition failed, trying popup", e);
        instance.acquireTokenPopup(request)
          .then((response) => {
            setIdToken(response.idToken);
            sessionStorage.setItem("Azure_AD_Access_Token", response.accessToken)
          })
          .catch((popupError) => {
            console.log("Popup token acquisition failed", popupError);
            setLoading(false);
            setErrorMessage("Authentication failed. Please try again.");
          });
      });
  }

  async function getUserData() {
    try {
      if (!accounts || accounts.length === 0) {
        setErrorMessage("No user account found");
        setAuthState(prev => ({ ...prev, validUser: false }));
        sessionStorage.clear();
        setLoading(false);
        return;
      }

      let email = accounts[0].username;
      sessionStorage.setItem("EmailId", email);

      // fetch data from Azure AD
      const azureData = await getUserInfo(email);
      if (!azureData) {
        setErrorMessage("User not found in Azure AD")
        setAuthState(prev => ({ ...prev, validUser: false }));
        sessionStorage.clear()
        // Clear the account from MSAL cache to force re-authentication
        const account = instance.getActiveAccount();
        if (account) {
          instance.setActiveAccount(null);
        }
        setLoading(false);
        return;
      }

      if (azureData?.displayName) {
        //Validate the system table user data        
        const result = await authenticate({ UserName: azureData?.displayName });
        if (result?.UserData && result?.UserData !== "") {
          // Store all user data for later department selection if needed
          sessionStorage.setItem("AllUserData", JSON.stringify(result.UserData));

          // Use common data from Azure AD and API response
          const commonUserData = {
            UserId: email.replace("@godrej.com", ""),
            MobileNo: result.UserData[0].MobileNo, // Same for all departments
            EmailId: email,
            UserName: azureData?.displayName,
            displayName: azureData?.displayName,
            Token: result?.Token,
            requiresDeptSelection: result.requiresDeptSelection
          };

          sessionStorage.setItem("PendingUserData", JSON.stringify(commonUserData));
          sessionStorage.setItem("UserName", azureData?.displayName);
          sessionStorage.setItem("Token", result?.Token);

          setUserData(commonUserData);
          setAuthState(prev => ({
            ...prev,
            allUserData: result.UserData,
            pendingAuth: commonUserData
          }));
          setLoading(false);
        } else {
          
          setAuthState(prev => ({ ...prev, validUser: false }));
          setErrorMessage("User validation failed. Please contact administrator.");
          sessionStorage.clear();
          // Clear the account from MSAL cache to force re-authentication
          const account = instance.getActiveAccount();
          if (account) {
            instance.setActiveAccount(null);
          }
          setLoading(false);
        }
      }
    } catch (err) {
      console.log("Error in getUserData", err);
      setAuthState(prev => ({ ...prev, validUser: false }));
      const errorMsg = err?.response?.data?.message || err?.message || "User validation failed. Please contact administrator.";
      setErrorMessage(errorMsg);
      sessionStorage.clear();
      // Clear the account from MSAL cache to force re-authentication
      const account = instance.getActiveAccount();
      if (account) {
        instance.setActiveAccount(null);
      }
      setLoading(false);
    }
  }

  // Complete authentication after access code verification and department selection (if required)
  const completeAuthentication = (selectedUserData = null) => {
    let userData = selectedUserData;

    // For single user, use the first user from allUserData
    if (!selectedUserData && authState.allUserData && authState.allUserData.length === 1) {
      userData = authState.allUserData[0];
    }

    if (userData && userData.DeptId) { // Only complete if DeptId exists
      sessionStorage.setItem("UserId", userData.UserId || "");
      sessionStorage.setItem("Email", userData.EmailId || "");
      sessionStorage.setItem("RoleId", userData.RoleId || "");
      sessionStorage.setItem("RoleName", userData.RoleName || "");
      sessionStorage.setItem("ZoneCode", userData.ZoneCode || "");
      sessionStorage.setItem("BranchCode", userData.BranchCode || "");
      sessionStorage.setItem("DivisionCode", userData.DivisionCode || "");
      sessionStorage.setItem("AccessCriteria", userData.AccessCriteria || "");
      sessionStorage.setItem("DeptId", userData.DeptId || "");
      sessionStorage.setItem("AuthorizedSessions", JSON.stringify(userData.AuthorizedSessions || []));
      sessionStorage.setItem("User", JSON.stringify(userData));
      sessionStorage.setItem("AccessCodeVerified", "true");

      sessionStorage.removeItem("PendingUserData");
      setAuthState({
        validUser: true,
        accessCodeVerified: true,
        deptSelectionRequired: false,
        pendingAuth: null,
        allUserData: null,
        availableDepts: []
      });
    }
  };

  const handleAccessCodeVerified = (verified) => {
    setAuthState(prev => ({ ...prev, accessCodeVerified: verified }));
    if (verified) {
      // Check if department selection is required after access code verification
      const storedAllUserData = sessionStorage.getItem('AllUserData');
      if (authState.pendingAuth?.requiresDeptSelection && storedAllUserData) {
        const allUsers = JSON.parse(storedAllUserData);
        if (allUsers.length > 1) {
          setAuthState(prev => ({
            ...prev,
            allUserData: allUsers,
            availableDepts: allUsers,
            deptSelectionRequired: true
          }));
          return;
        }
      }
      completeAuthentication();
    }
  };

  const handleDeptSelected = (result) => {
    if (result?.UserData) {
      // Complete authentication with selected department data
      completeAuthentication(result.UserData);
    }
  };

  const handleBackToLogin = () => {
    setAuthState({
      validUser: false,
      accessCodeVerified: false,
      deptSelectionRequired: false,
      pendingAuth: null,
      allUserData: null,
      availableDepts: []
    });
    setUserData(false);
    sessionStorage.clear();
    instance.logoutRedirect();
  };

  console.log('Render state:', authState);

  return (
    <>
      {loading && <Loader />}
      <AuthenticatedTemplate>
        {authState.pendingAuth && !authState.accessCodeVerified && userData && (
          <AccessCodeVerification
            userData={userData}
            setAccessCodeVerified={handleAccessCodeVerified}
            onBack={handleBackToLogin}
          />
        )}
        {authState.deptSelectionRequired && authState.accessCodeVerified && authState.availableDepts.length > 0 && (
          <DepartmentSelection
            users={authState.availableDepts}
            token={sessionStorage.getItem("Token")}
            onDeptSelected={handleDeptSelected}
            onBack={handleBackToLogin}
          />
        )}
        {authState.validUser && authState.accessCodeVerified && !authState.deptSelectionRequired && <Home />}
        {/* Fallback for any unhandled state */}
        {!authState.pendingAuth && !authState.validUser && !authState.deptSelectionRequired && (
          <LoginPage loginErrorMessage={errorMessage} />
        )}
        {/* Show login if department selection fails */}
        {authState.accessCodeVerified && authState.deptSelectionRequired && authState.availableDepts.length === 0 && (
          <LoginPage loginErrorMessage="Session expired. Please login again." />
        )}
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LoginPage loginErrorMessage={errorMessage} />
      </UnauthenticatedTemplate>
    </>
  );
}

export default App;